"use client"

import {
  CheckCircle2,
  ChevronDown,
  Copy,
  Loader2,
  Mail,
  MessageSquare,
  Send,
  Shield,
  Sparkles,
  Zap
} from "lucide-react"
import type React from "react"
import { useState } from "react"
import { toast } from "sonner"

import { Button } from "@/core/ui/button"
import { Input } from "@/core/ui/input"
import { Label } from "@/core/ui/label"
import { Textarea } from "@/core/ui/textarea"
import { cn } from "@/core/utils/utils"

import { contactFaqs, getFaqJsonLd } from "./faq-data"

export default function Contact() {
  const [selectedProjectType, setSelectedProjectType] = useState<string>("Custom AI Agent")
  const [selectedBudget, setSelectedBudget] = useState<string>("$10k - $25k")
  const [openFaqId, setOpenFaqId] = useState<string | null>("privacy-ip")
  const [focusedField, setFocusedField] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)

  const projectTypes = [
    "Custom AI Agent",
    "RAG Knowledge Base",
    "Full-Stack Platform",
    "Fractional CTO"
  ]

  const budgetOptions = [
    "$5k - $10k",
    "$10k - $25k",
    "$25k+",
    "$3,000/mo Retainer"
  ]

  const toggleFaq = (id: string) => {
    // Keep at least one accordion open (fallback to first item) to prevent layout fluctuation
    setOpenFaqId(openFaqId === id ? "privacy-ip" : id)
  }

  const handleCopyEmail = () => {
    navigator.clipboard.writeText("contact@hamidayub.com")
    toast.success("Email copied to clipboard!", { description: "contact@hamidayub.com" })
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setIsLoading(true)

    const formData = new FormData(e.currentTarget)
    const rawMessage = formData.get("message")
    const formattedMessage = `${rawMessage}\n\n--- Project Details ---\n• Project Type: ${selectedProjectType}\n• Budget Range: ${selectedBudget}`

    const data = {
      name: formData.get("name"),
      email: formData.get("email"),
      message: formattedMessage,
      projectType: selectedProjectType,
      budget: selectedBudget
    }

    try {
      const response = await fetch("/api/send", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(data)
      })

      if (!response.ok) {
        throw new Error("Failed to send message")
      }

      setIsSuccess(true)
      toast.success("Message Received", {
        description: "I will review your project details and reply within 24 hours."
      })
      e.currentTarget.reset()
    } catch (error) {
      toast.error("Submission Failed", {
        description: "Direct message failed. Please email contact@hamidayub.com directly."
      })
      console.error(error)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <section id="contact" className="relative py-24 bg-[#050505] overflow-hidden border-t border-white/5" style={{ contain: "paint" }}>
      {/* AI SEO & GEO FAQ JSON-LD Structured Data Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(getFaqJsonLd()) }}
      />

      {/* Subtle Background Lighting */}
      <div className="absolute top-1/3 left-0 w-[400px] h-[400px] bg-cyan-900/10 rounded-full blur-[140px] pointer-events-none" />
      <div className="absolute bottom-10 right-0 w-[400px] h-[400px] bg-indigo-900/10 rounded-full blur-[140px] pointer-events-none" />

      <div className="container relative z-10 px-4 md:px-8 mx-auto w-full max-w-6xl">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 mb-4">
            <Sparkles className="w-3.5 h-3.5 text-cyan-400" />
            <span className="text-[10px] uppercase tracking-widest font-semibold text-white/70">Get In Touch</span>
          </div>
          <h2 className="text-3xl md:text-5xl font-medium tracking-tight text-white mb-4">
            Ready to deploy <span className="italic font-serif text-transparent bg-clip-text bg-gradient-to-r from-white via-white/80 to-white/40">production AI?</span>
          </h2>
          <p className="text-base text-white/50 max-w-xl mx-auto font-light leading-relaxed">
            Direct access to Principal-level engineering. Explore answers to common questions or send a direct message.
          </p>
        </div>

        {/* 2-Column Grid Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-start">
          
          {/* Left Column: Community Researched FAQ Engine */}
          <div className="lg:col-span-6 space-y-6">
            <div className="flex items-center justify-between pb-2 border-b border-white/10">
              <h3 className="text-lg font-semibold tracking-tight text-white flex items-center gap-2">
                <Shield className="w-4 h-4 text-cyan-400" />
                Frequently Asked Questions
              </h3>
            </div>

            <div className="space-y-3">
              {contactFaqs.map((faq) => {
                const isOpen = openFaqId === faq.id
                return (
                  <div
                    key={faq.id}
                    className={cn(
                      "rounded-2xl border transition-all duration-300 overflow-hidden bg-[#0c0c0c]",
                      isOpen ? "border-white/20 shadow-lg bg-[#0f0f0f]" : "border-white/5 hover:border-white/15"
                    )}
                  >
                    <button
                      onClick={() => toggleFaq(faq.id)}
                      className="w-full p-5 text-left flex items-center justify-between gap-4 cursor-pointer"
                    >
                      <div className="flex items-center gap-3">
                        <span className="px-2 py-0.5 rounded text-[9px] uppercase tracking-widest font-mono font-semibold bg-white/5 border border-white/10 text-white/50">
                          {faq.category}
                        </span>
                        <h4 className="text-sm font-medium text-white/90 leading-snug">{faq.question}</h4>
                      </div>
                      <ChevronDown className={cn("w-4 h-4 text-white/40 shrink-0 transition-transform duration-300", isOpen && "rotate-180 text-cyan-400")} />
                    </button>

                    {isOpen && (
                      <div className="px-5 pb-5 pt-1 border-t border-white/5 text-xs text-white/60 font-light leading-relaxed animate-in fade-in duration-300">
                        {faq.answer}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>

            {/* Direct Quick Contact Footer */}
            <div className="p-5 rounded-2xl bg-white/[0.02] border border-white/5 flex flex-col sm:flex-row items-center justify-between gap-4 mt-6">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-xl bg-cyan-500/10 border border-cyan-500/20 text-cyan-400">
                  <Mail className="w-4 h-4" />
                </div>
                <div>
                  <p className="text-xs font-medium text-white">Direct Email</p>
                  <p className="text-[11px] text-white/55 font-mono">contact@hamidayub.com</p>
                </div>
              </div>

              <Button
                variant="outline"
                size="sm"
                onClick={handleCopyEmail}
                className="w-full sm:w-auto h-9 rounded-xl border-white/10 bg-white/5 hover:bg-white/10 text-white text-xs gap-2"
              >
                <Copy className="w-3.5 h-3.5" />
                Copy Email
              </Button>
            </div>
          </div>

          {/* Right Column: Executive Intake Form */}
          <div className="lg:col-span-6 w-full">
            <div className="rounded-[28px] border border-white/10 bg-[#0a0a0a] shadow-2xl p-6 md:p-8 relative overflow-hidden">
              
              <div className="mb-6">
                <h3 className="text-xl md:text-2xl font-bold tracking-tight text-white mb-1">Start Your Project</h3>
                <p className="text-xs text-white/50 font-light">Select your parameters below to receive a direct response within 24 hours.</p>
              </div>

              {isSuccess ? (
                <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-2xl p-8 text-center space-y-4 my-8">
                  <div className="w-12 h-12 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto">
                    <CheckCircle2 className="w-6 h-6 text-emerald-400" />
                  </div>
                  <h4 className="text-lg font-semibold text-white">Message Received!</h4>
                  <p className="text-xs text-white/60 font-light max-w-sm mx-auto">
                    Thank you. I have received your details for <span className="text-emerald-300 font-medium">{selectedProjectType}</span> and will get back to you shortly.
                  </p>
                  <Button
                    variant="outline"
                    className="mt-4 border-white/10 bg-white/5 text-white hover:bg-white/10 rounded-xl text-xs"
                    onClick={() => setIsSuccess(false)}
                  >
                    Send Another Message
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <input type="hidden" name="projectType" value={selectedProjectType} />
                  <input type="hidden" name="budget" value={selectedBudget} />
                  
                  {/* Triage Pills 1: Project Type */}
                  <div className="space-y-2">
                    <Label className="text-xs font-medium text-white/60 uppercase tracking-wider text-[10px]">
                      1. Project Type
                    </Label>
                    <div className="grid grid-cols-2 gap-2">
                      {projectTypes.map((type) => (
                        <button
                          key={type}
                          type="button"
                          onClick={() => setSelectedProjectType(type)}
                          className={cn(
                            "px-3 py-2 rounded-xl text-xs font-medium border text-left transition-all duration-200 cursor-pointer",
                            selectedProjectType === type
                              ? "bg-white text-black border-white shadow-md font-semibold"
                              : "bg-white/[0.03] border-white/10 text-white/70 hover:border-white/30"
                          )}
                        >
                          {type}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Triage Pills 2: Budget Range */}
                  <div className="space-y-2">
                    <Label className="text-xs font-medium text-white/60 uppercase tracking-wider text-[10px]">
                      2. Estimated Budget
                    </Label>
                    <div className="grid grid-cols-2 gap-2">
                      {budgetOptions.map((budget) => (
                        <button
                          key={budget}
                          type="button"
                          onClick={() => setSelectedBudget(budget)}
                          className={cn(
                            "px-3 py-2 rounded-xl text-xs font-medium border text-left transition-all duration-200 cursor-pointer",
                            selectedBudget === budget
                              ? "bg-amber-500/20 text-amber-300 border-amber-500/50 font-semibold"
                              : "bg-white/[0.03] border-white/10 text-white/70 hover:border-white/30"
                          )}
                        >
                          {budget}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Contact Fields */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                    <div className="space-y-1.5">
                      <Label htmlFor="name" className="text-[11px] font-medium text-white/60">Your Name</Label>
                      <Input
                        id="name"
                        name="name"
                        placeholder="Alex Vance"
                        required
                        disabled={isLoading}
                        onFocus={() => setFocusedField("name")}
                        onBlur={() => setFocusedField(null)}
                        className={cn(
                          "h-11 rounded-xl bg-white/[0.03] border-white/10 text-white placeholder:text-white/20 text-xs transition-all duration-200",
                          focusedField === "name" && "border-white/40 ring-1 ring-white/20"
                        )}
                      />
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="email" className="text-[11px] font-medium text-white/60">Work Email</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        placeholder="alex@company.com"
                        required
                        disabled={isLoading}
                        onFocus={() => setFocusedField("email")}
                        onBlur={() => setFocusedField(null)}
                        className={cn(
                          "h-11 rounded-xl bg-white/[0.03] border-white/10 text-white placeholder:text-white/20 text-xs transition-all duration-200",
                          focusedField === "email" && "border-white/40 ring-1 ring-white/20"
                        )}
                      />
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="message" className="text-[11px] font-medium text-white/60">Project Description / Message</Label>
                    <Textarea
                      id="message"
                      name="message"
                      placeholder="Briefly describe your goals, timeline, or architecture requirements..."
                      required
                      disabled={isLoading}
                      rows={3}
                      className={cn(
                        "rounded-xl bg-white/[0.03] border-white/10 text-white placeholder:text-white/20 text-xs transition-all duration-200 resize-none",
                        focusedField === "message" && "border-white/40 ring-1 ring-white/20"
                      )}
                      onFocus={() => setFocusedField("message")}
                      onBlur={() => setFocusedField(null)}
                    />
                  </div>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    disabled={isLoading}
                    className="w-full h-14 rounded-xl font-semibold text-sm transition-all duration-300 bg-white text-black hover:bg-zinc-200 hover:scale-[1.005] shadow-lg group relative overflow-hidden mt-2"
                  >
                    {isLoading ? (
                      <span className="flex items-center justify-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin text-black/60" />
                        Sending Message...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        Send Message
                        <Send className="h-4 w-4 opacity-70 group-hover:translate-x-1 transition-transform" />
                      </span>
                    )}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
